import omit from 'lodash-es/omit';

import type {
  ConditionSection,
  ConditionSelection,
  ConditionSelectionType,
  ConditionSet,
  ConditionValue,
} from '../conditions.types';
import {
  CONDITION_SELECTION_TYPES,
  isSelectValue,
} from './conditionSelection.util';
import {
  deselectCondition,
  updateConditionSelection,
} from './updateConditionSelection';

/* Set-level counterparts to the conditionSelection utils */

export const createConditionSet = (applyTo: string): ConditionSet => ({
  applyTo,
  selections: { excludes: {}, includes: {} },
});

const updateSetAt = (
  sets: ConditionSet[],
  index: number,
  update: (set: ConditionSet) => ConditionSet
): ConditionSet[] => {
  const set = sets[index];

  if (!set) {
    return sets;
  }

  const updated = [...sets];
  updated[index] = update(set);
  return updated;
};

export const addConditionSet = (
  sets: ConditionSet[],
  applyTo: string
): ConditionSet[] => [...sets, createConditionSet(applyTo)];

export const removeConditionSet = (
  sets: ConditionSet[],
  index: number
): ConditionSet[] => {
  const remaining = [...sets];
  remaining.splice(index, 1);
  return remaining;
};

/** Places the copy after its source, so a duplicate reads as the next group */
export const duplicateConditionSet = (
  sets: ConditionSet[],
  index: number
): ConditionSet[] => {
  const source = sets[index];

  if (!source) {
    return sets;
  }

  const duplicated = [...sets];

  duplicated.splice(index + 1, 0, {
    applyTo: source.applyTo,
    selections: {
      excludes: { ...source.selections.excludes },
      includes: { ...source.selections.includes },
    },
  });

  return duplicated;
};

/**
 * Whether a section can hold this selection type: a locked one only its own,
 * an unlocked one always includes, and excludes when `allowExcludes` is set.
 */
const canHold = (
  section: ConditionSection,
  selectionType: ConditionSelectionType
): boolean =>
  section.selectionType
    ? section.selectionType === selectionType
    : selectionType === 'includes' || !!section.allowExcludes;

/**
 * Drops selections these sections can't hold, so a section the editor stopped
 * rendering can't leave behind a value that still saves.
 */
export const pruneToSections = (
  selections: ConditionSelection,
  sections: ConditionSection[]
): ConditionSelection =>
  CONDITION_SELECTION_TYPES.reduce(
    (pruned, selectionType) => ({
      ...pruned,
      [selectionType]: Object.fromEntries(
        sections
          .filter(
            (section) =>
              canHold(section, selectionType) &&
              selections[selectionType][section.key] !== undefined
          )
          .map((section) => [
            section.key,
            selections[selectionType][section.key],
          ])
      ),
    }),
    {} as ConditionSelection
  );

/** `sections` belong to the new "Apply to" mode; what it can't hold is dropped */
export const updateConditionSetApplyTo = (
  sets: ConditionSet[],
  index: number,
  applyTo: string,
  sections: ConditionSection[]
): ConditionSet[] =>
  updateSetAt(sets, index, (set) => ({
    applyTo,
    selections: pruneToSections(set.selections, sections),
  }));

/** Once the user edits a simplified section, its notice has done its job */
const withoutOverlap = (set: ConditionSet, key: string): ConditionSet =>
  set.overlaps?.[key] ? { ...set, overlaps: omit(set.overlaps, key) } : set;

export const updateConditionSetSelection = (
  sets: ConditionSet[],
  index: number,
  selectionType: ConditionSelectionType,
  key: string,
  value: ConditionValue
): ConditionSet[] =>
  updateSetAt(sets, index, (set) => ({
    ...withoutOverlap(set, key),
    selections: updateConditionSelection(
      set.selections,
      selectionType,
      key,
      value
    ),
  }));

/** Clears one section's selections within a single set */
export const deselectConditionSetSection = (
  sets: ConditionSet[],
  index: number,
  key: string
): ConditionSet[] =>
  updateSetAt(sets, index, (set) => ({
    ...withoutOverlap(set, key),
    selections: deselectCondition(set.selections, key),
  }));

/**
 * One section's selected values across all OR'd groups, deduped, so a caller
 * that hydrates them (products, brands) can fetch once for every group.
 */
export const getSelectedValues = (
  sets: ConditionSet[],
  selectionType: ConditionSelectionType,
  key: string
): string[] => [
  ...new Set(
    sets.flatMap((set) => {
      const selected = set.selections[selectionType][key];

      return isSelectValue(selected) ? selected : [];
    })
  ),
];
